import { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";

/**
 * The Facebook pages this browser has connected (the SHOP_PAGE cookie), kept current.
 *
 * Screens read the cookie once when they opened, so a page connected afterwards only showed
 * after a refresh. Whatever writes the cookie calls `announceShopPages()`; the hook also
 * re-reads on window focus (the Facebook login popup returns focus) and every few seconds as
 * a backstop, comparing the raw text so nothing re-renders when it has not changed.
 */
//* How long this browser keeps the page list. Page tokens themselves do not expire on a timer
//* (they come from a long-lived user token); Facebook drops app access after 90 days unused.
export const SHOP_PAGE_DAYS = 90;
//* Cookie set by "ຍົກເລີກການເຊື່ອມຕໍ່": stops the Navbar reconnecting on its own after a login.
export const FB_DISCONNECTED = "FB_DISCONNECTED";

const EVENT = "shop-pages-changed";
const POLL_MS = 3000;

/**
 * WHY PAGES WENT MISSING: the list used to live only in the SHOP_PAGE cookie, with every field
 * /me/accounts returns (token, category, category_list, tasks…). That is ~750 bytes a page,
 * and a browser rejects any cookie over 4 KB — silently. Picking 3–5 pages produced a cookie
 * too big to store, so the browser kept the OLD one: the shop saw one page, or a few.
 *
 * Now: only the fields any screen reads are kept, the full list goes to localStorage (MBs, no
 * per-item limit), and readers look there first. The cookie is still written for anything that
 * reads it directly, and dropped rather than left stale when the list no longer fits.
 */
const KEY = "SHOP_PAGE";
const COOKIE_MAX_BYTES = 3800; // under the 4096 per-cookie limit, leaving room for name/attrs

const slimPage = (p) => ({
    id: p.id,
    name: p.name,
    access_token: p.access_token,
    ...(p.isPrivate ? { isPrivate: true } : {}),
});

/** Raw JSON text of the page list: localStorage first, the cookie for older sessions. */
export const readShopPagesRaw = () => {
    try {
        const local = localStorage.getItem(KEY);
        if (local) return local;
    } catch (e) { /* storage blocked */ }
    return Cookies.get(KEY) || "";
};

/** Store the page list for every screen, and tell the ones already open. */
export const saveShopPages = (pages) => {
    const list = (Array.isArray(pages) ? pages : []).filter((p) => p && p.id).map(slimPage);
    const json = JSON.stringify(list);
    try { localStorage.setItem(KEY, json); } catch (e) { /* storage blocked: cookie only */ }
    //* Pages saved = connected again, whichever screen's connect button did it.
    if (list.some((p) => p.access_token)) Cookies.remove(FB_DISCONNECTED);
    if (encodeURIComponent(json).length < COOKIE_MAX_BYTES) Cookies.set(KEY, json, { expires: SHOP_PAGE_DAYS });
    else Cookies.remove(KEY);
    announceShopPages();
    return list;
};

const readRaw = () => readShopPagesRaw();
export const parsePages = (raw) => {
    try {
        const v = raw ? JSON.parse(raw) : [];
        return Array.isArray(v) ? v : [];
    } catch (e) {
        return [];
    }
};

/** Call right after writing the SHOP_PAGE cookie. */
export const announceShopPages = () => {
    try { window.dispatchEvent(new Event(EVENT)); } catch (e) { /* no window */ }
};

export const useShopPages = () => {
    const [raw, setRaw] = useState(readRaw);
    useEffect(() => {
        const sync = () => setRaw((prev) => { const now = readRaw(); return now === prev ? prev : now; });
        window.addEventListener(EVENT, sync);
        window.addEventListener("focus", sync);
        const timer = setInterval(sync, POLL_MS);
        return () => {
            window.removeEventListener(EVENT, sync);
            window.removeEventListener("focus", sync);
            clearInterval(timer);
        };
    }, []);
    return useMemo(() => parsePages(raw), [raw]);
};

/**
 * Every page the Facebook user granted, not just the first screen of them.
 *
 * /me/accounts returns 25 pages per call by default and the rest behind `paging.cursors.after`;
 * reading only `data` silently dropped every page past the 25th. Calls back once with
 * `{ data: allPages }` (or `{ data: whatWeGot, error }`), the same shape FB.api gives, so call
 * sites only swap the function name.
 */
const MAX_ACCOUNT_CALLS = 20; // 2,000 pages — a guard against a cursor that never ends
export const fetchAllFbPages = (userToken, callback) => {
    const all = [];
    const next = (after, calls) => {
        const cursor = after ? `&after=${encodeURIComponent(after)}` : "";
        window.FB.api(`/me/accounts?limit=100${cursor}&access_token=${userToken}`, (res) => {
            if (!res || res.error) {
                callback({ data: all, error: res && res.error });
                return;
            }
            all.push(...(res.data || []));
            const nextAfter = res.paging && res.paging.next && res.paging.cursors && res.paging.cursors.after;
            if (nextAfter && calls < MAX_ACCOUNT_CALLS) next(nextAfter, calls + 1);
            else callback({ data: all });
        });
    };
    next(null, 1);
};

/**
 * True when the shop has connected pages with tokens on this browser.
 *
 * Live and Post only use page tokens for their Graph calls; the user token from
 * FB.getLoginStatus was just a gate, and it is often missing after the app is signed out and in
 * again (Safari and newer Chrome hide the Facebook session from the SDK). The page list is
 * restored from the DB on login, so it is the honest "still connected" signal.
 */
export const hasConnectedPages = (list) =>
    Array.isArray(list) && list.some((p) => p && p.id && p.access_token);
